// The public pages (no login), in one place.
//
// - App.js draws them (each one is its own code-split chunk).
// - scripts/prerender.js renders them to HTML at build time, so search
//   engines get the real text (see docs/SEO_PUBLIC_PAGES.md).
// - index.js preloads the page being opened before mounting React, so the
//   prerendered HTML is replaced by the live page without a spinner.
//
// Their titles, descriptions and addresses for search engines live in
// lib/seoPages.json; seo.test.js checks the two lists agree.
import lazyWithReload from './lib/lazyWithReload';
import { findPage, pageLanguages } from './lib/seo';
import cfg from './lib/seoPages.json';
import { DEFAULT_LANGUAGE, localizedPath } from './lib/siteLanguages';

const route = (path, load, extra = {}) => ({ path, load, Component: lazyWithReload(load), ...extra });

export const PUBLIC_ROUTES = [
  route('/', () => import('./pages/LandingPage')),
  route('/funcionalidades', () => import('./pages/Funcionalidades')),
  route('/sistema-verifactu', () => import('./pages/SistemaVerifactu')),
  route('/calculadoras', () => import('./pages/Calculadoras')),
  route('/calendario-fiscal', () => import('./pages/CalendarioFiscal')),
  route('/privacy', () => import('./pages/Privacy')),
  route('/aviso-legal', () => import('./pages/AvisoLegal')),
  route('/condiciones-legales', () => import('./pages/CondicionesLegales')),
  route('/politica-de-cookies', () => import('./pages/PoliticaCookies')),
  // Guides, articles and product pages: content in src/content/pages/.
  route('/blog', () => import('./content/pages/blog')),
  route('/blog/verifactu-aplazamiento-2028', () => import('./content/pages/verifactuAplazamiento2028')),
  route('/verifactu/app-gratuita-aeat', () => import('./content/pages/appGratuitaAeat')),
  route('/factura-electronica-obligatoria-b2b', () => import('./content/pages/facturaElectronicaB2b')),
  route('/software-facturacion-gestorias', () => import('./content/pages/gestorias')),
  route('/programa-facturacion-tpv', () => import('./content/pages/tpv')),
  route('/lector-ocr-facturas-gastos', () => import('./content/pages/lectorOcr')),
  route('/programa-facturacion-tienda-online', () => import('./content/pages/tiendaOnline')),
  route('/alternativa-a-quipu', () => import('./content/pages/alternativaQuipu')),
  route('/alternativa-a-holded', () => import('./content/pages/alternativaHolded')),
  // Sign-in and the ZPOS licence page answer 200 with their own titles but
  // have nothing to read for a search engine: not drawn at build time.
  route('/auth', () => import('./pages/Auth'), { prerender: false }),
  route('/licencia', () => import('./pages/LicenseRenewal'), { prerender: false }),
];

const byPath = new Map(PUBLIC_ROUTES.map((r) => [r.path, r]));

// The other language versions of the website's pages (lib/siteLanguages.js):
// '/fr/funcionalidades' for every language a page exists in (seoPages.json
// `langs`), and '/es/…' as an alias of the Spanish page. Same components:
// each one reads in the language of its address (components/public/SiteLang).
// Pages marked `app` (sign-in, licence) have no language versions.
export const LOCALIZED_ROUTES = cfg.pages
  .filter((page) => !page.app && byPath.has(page.path))
  .flatMap((page) => pageLanguages(page).map((lang) => {
    const prefixed = lang === DEFAULT_LANGUAGE
      ? `/es${page.path === '/' ? '' : page.path}`
      : localizedPath(page.path, lang).replace(/\/$/, '');
    return { path: prefixed, lang, base: page.path, Component: byPath.get(page.path).Component };
  }));

// The lazy component of a public page, by its canonical address.
export function publicPage(path) {
  const r = byPath.get(path);
  if (!r) throw new Error(`no public route "${path}"`);
  return r.Component;
}

// Loads the code of the public page at `pathname` (or its alias). Resolves to
// null when the address is not a public page.
export function preloadPublicRoute(pathname) {
  const page = findPage(pathname);
  const r = page && byPath.get(page.path);
  return r ? r.Component.preload() : Promise.resolve(null);
}
